import { FC, useState } from "react";
import styled from "@emotion/styled";
import { Button } from "@/components/button";
import {
  SHARED_ITEM_INVITE_STATUSES,
  SHARED_ITEM_KIND_SELECTABLE,
} from "@/pages/utilities/shared-item-invites/shared/constants";
import { Flex, Select, Switch, Text, TextField } from "@radix-ui/themes";

type SharedItemInviteSearchFilters = {
  inviterSpaceAccountId?: string;
  sharedItemId?: string;
  sharedItemKind?: string;
  status?: string;
  includeRevoked?: boolean;
};

interface SharedItemInviteSearchFormProps {
  initialFilters: SharedItemInviteSearchFilters;
  onSearch: (filters: SharedItemInviteSearchFilters) => void;
  primarySpaceAccountId?: string | null;
  isPrimaryLoading?: boolean;
}

export const SharedItemInviteSearchForm: FC<SharedItemInviteSearchFormProps> = ({
  initialFilters,
  onSearch,
  primarySpaceAccountId,
  isPrimaryLoading,
}) => {
  const [inviterSpaceAccountId, setInviterSpaceAccountId] = useState(
    initialFilters.inviterSpaceAccountId ?? ""
  );
  const [sharedItemId, setSharedItemId] = useState(initialFilters.sharedItemId ?? "");
  const [sharedItemKind, setSharedItemKind] = useState(initialFilters.sharedItemKind ?? "");
  const [status, setStatus] = useState(initialFilters.status ?? "");
  const [includeRevoked, setIncludeRevoked] = useState(initialFilters.includeRevoked ?? true);

  const ANY_KIND_VALUE = "ANY_KIND";
  const ANY_STATUS_VALUE = "ANY_STATUS";

  const resolvedSharedItemKind = sharedItemKind || ANY_KIND_VALUE;
  const resolvedStatus = status || ANY_STATUS_VALUE;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    onSearch({
      inviterSpaceAccountId: inviterSpaceAccountId.trim() || undefined,
      sharedItemId: sharedItemId.trim() || undefined,
      sharedItemKind: sharedItemKind || undefined,
      status: status || undefined,
      includeRevoked,
    });
  };

  const handleReset = () => {
    setInviterSpaceAccountId("");
    setSharedItemId("");
    setSharedItemKind("");
    setStatus("");
    setIncludeRevoked(true);
    onSearch({ includeRevoked: true });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FieldGroup>
        <Field>
          <Label>Inviter Space Account ID</Label>
          <Flex gap="2" align="center">
            <TextField.Root
              placeholder="5ad2a64b-..."
              value={inviterSpaceAccountId}
              onChange={event => setInviterSpaceAccountId(event.currentTarget.value)}
              style={{ flex: 1 }}
            />
            <Button
              type="button"
              variant="soft"
              onClick={() =>
                primarySpaceAccountId && setInviterSpaceAccountId(primarySpaceAccountId)
              }
              disabled={!primarySpaceAccountId || isPrimaryLoading}
            >
              Fill space account
            </Button>
          </Flex>
        </Field>
        <Field>
          <Label>Shared Item ID</Label>
          <TextField.Root
            placeholder="e3f1..."
            value={sharedItemId}
            onChange={event => setSharedItemId(event.currentTarget.value)}
          />
        </Field>
      </FieldGroup>

      <FieldGroup>
        <Field>
          <Label>Shared Item Kind</Label>
          <Select.Root
            value={resolvedSharedItemKind}
            onValueChange={value => {
              if (value === ANY_KIND_VALUE) {
                setSharedItemKind("");
                return;
              }
              setSharedItemKind(value);
            }}
          >
            <Select.Trigger placeholder="Any" />
            <Select.Content>
              <Select.Item value={ANY_KIND_VALUE}>Any</Select.Item>
              {SHARED_ITEM_KIND_SELECTABLE.map(kind => (
                <Select.Item key={kind} value={kind}>
                  {kind}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Field>
        <Field>
          <Label>Status</Label>
          <Select.Root
            value={resolvedStatus}
            onValueChange={value => {
              if (value === ANY_STATUS_VALUE) {
                setStatus("");
                return;
              }
              setStatus(value);
            }}
          >
            <Select.Trigger placeholder="Any" />
            <Select.Content>
              <Select.Item value={ANY_STATUS_VALUE}>Any</Select.Item>
              {SHARED_ITEM_INVITE_STATUSES.map(state => (
                <Select.Item key={state} value={state}>
                  {state}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Field>
      </FieldGroup>

      <Flex align="center" gap="2">
        <Switch
          checked={includeRevoked}
          onCheckedChange={setIncludeRevoked}
          size="1"
          aria-label="Include revoked"
        />
        <Text size="2">Include revoked invites</Text>
      </Flex>

      <ButtonRow>
        <Button type="submit">Search</Button>
        <Button type="button" variant="ghost" onClick={handleReset}>
          Reset
        </Button>
      </ButtonRow>
    </Form>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 260px;
`;

const Label = styled(Text)`
  font-size: 14px;
  color: var(--gray-11);
`;

const ButtonRow = styled(Flex)`
  gap: 12px;
`;

export type { SharedItemInviteSearchFilters };
